import {
  type PaymentOrder,
  type SolidgatePaymentModalRequirements,
  type SolidgatePaymentModalStatus,
  type SolidgateRequestPaymentResponse,
} from '@/types/payment';

import { RootState } from '@/store/store';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { merge } from 'lodash';

export interface PaymentState {
  // Order used for:
  // - redirecting to the order single (if `id` is provided)
  // - get `orderSnapshot` or `sandboxSnapshot` id for payment
  order?: PaymentOrder | null;
  paymentDetails?: SolidgateRequestPaymentResponse; // Optional payment details for Solidgate payment modal
  /**
   * TODO: Rename "*success*" to "*status*" -> make modal more generic!
   */
  status: SolidgatePaymentModalStatus; // Optional success flag for custom success Solidgate payment modal

  cardPreferences: {
    shouldSaveCard: boolean; // Flag to indicate if the card should be saved for future payments
    isPrimary: boolean; // Flag to indicate if the payment card should be set as primary
  };
}

export const paymentInitialState: PaymentState = {
  status: {
    id: undefined, // set any value to show the modal for debug!
    code: undefined,
    idOrder: undefined,
  },
  cardPreferences: {
    shouldSaveCard: true, // Default to not saving card details
    isPrimary: true, // Default to not setting the card as primary
  },
};

export const paymentSlice = createSlice({
  name: 'payment',
  initialState: paymentInitialState,
  reducers: {
    /**
     * Show the payment modal with merchant data and order to pay
     * @see SolidgatePaymentModal
     * @see useSolidgatePaymentModal
     */
    setOrderToPay: (state: PaymentState, { payload }: PayloadAction<SolidgatePaymentModalRequirements>) => {
      // set merchant data and order to pay -> this will be used to display the payment modal
      state.paymentDetails = payload?.paymentDetails;
      state.order = payload?.order;
    },
    // Close the payment modal and reset merchant data and order
    resetPaymentDetails: (state: PaymentState) => {
      // Reset all
      state.paymentDetails = undefined;
    },
    setPaymentStatus: (state: PaymentState, { payload }: PayloadAction<SolidgatePaymentModalStatus>) => {
      // Set success flag for custom success Solidgate payment modal
      state.status = payload;
    },
    setCardPreferences: (state: PaymentState, { payload }: PayloadAction<PaymentState['cardPreferences']>) => {
      state.cardPreferences = payload;
    },
  },
});

export default paymentSlice.reducer;

export const { setOrderToPay, resetPaymentDetails, setPaymentStatus, setCardPreferences } = paymentSlice.actions;

// Selector
export const selectAllPaymentState = ({ payment }: RootState) => payment;
export const selectSolidgatePaymentStatus = ({ payment }: RootState) => payment.status;
export const selectCardPreferences = ({ payment }: RootState) => merge({}, paymentInitialState.cardPreferences, payment.cardPreferences);
